import { useEffect, useRef, useState } from "react"

/**
 * The single clock behind an assembling screen.
 *
 * One `requestAnimationFrame` loop produces one number, and every glyph, label
 * and button on the screen is a pure function of it — see `utils/glyphMotion`.
 * A timer per element would be a hundred timers that drift apart within a
 * second; this cannot, because there is only one.
 *
 * The reference doc runs its clock at 0.9× the stated duration so the arc
 * finishes slightly before the screen is done with, rather than exactly as it
 * changes. That factor lives here, so callers state the wall-clock time the
 * screen is up for and get an animation that lands inside it.
 *
 * Under `prefers-reduced-motion` there is no loop at all: `p` is 1 from the
 * first render, and the caller renders its finished state. Read at mount rather
 * than corrected in an effect — a reader who asked for less motion must never
 * see a frame of the animation before it snaps.
 *
 * `active: false` is the other way to get no loop, and it exists because the
 * caller is not always allowed to skip the call. `RoundTransition` has to run
 * its motion hook whether or not there is a curtain — hooks cannot be
 * conditional — and it holds nothing to assemble in between, so an
 * unconditional loop spent a full arc's worth of frames re-rendering a
 * component that returns null. Once on mount, and again every time a curtain
 * was dismissed.
 */

/** The arc completes this far into the screen's life. From the reference. */
const ARC = 0.9

export function useAssembly(durationMs: number, replayKey: unknown = 0, active = true): number {
  const [reduced] = useState(
    () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
  )
  /* Idle and reduced both start finished: there is no arc to run, and a caller
     that renders anything at all should render its settled state. */
  const still = reduced || !active
  const [progress, setProgress] = useState(() => (still ? 1 : 0))
  const [playing, setPlaying] = useState(replayKey)
  const frame = useRef(0)

  /*
   * Reset during the render that changes the key, not in the effect that
   * follows it. `replayKey` changing means a different screen, and an effect
   * runs after the browser has already painted — the new screen's first frame
   * would show the previous one's motif fully assembled before it scattered.
   *
   * This is also what starts an arc that was idle: a caller with nothing to
   * assemble has nothing to key on either, so `active` turning true arrives
   * with a new key.
   */
  if (playing !== replayKey) {
    setPlaying(replayKey)
    setProgress(still ? 1 : 0)
  }

  useEffect(() => {
    if (still) return

    const started = performance.now()

    const tick = () => {
      const p = Math.min(1, (performance.now() - started) / (durationMs * ARC))
      setProgress(p)
      /* The loop stops itself at the end of the arc. Nothing on these screens
         moves afterwards, so a permanent rAF would be a permanent repaint. */
      if (p < 1) frame.current = requestAnimationFrame(tick)
    }

    frame.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame.current)
  }, [durationMs, replayKey, still])

  return progress
}
